import React from 'react'
import './App.css'
import "./css/Controls.css"
import Config from './library/Config'
import * as SearchParamUtil from './library/SearchParamUtil'
import { Upload } from "./interface/Upload"
import { Ad } from "./interface/Ad"
import * as Requests from './library/Requests'
import * as UploadUtil from "./library/UploadUtil"
import DisplayController from './components/DisplayController'
import Spinner from './components/Spinner'
import Controls from './components/settings/Controls'
import ReactGA from 'react-ga'
import ErrorView from './components/ErrorView'

interface AppState {
  uploads: Upload[]
  ads: Ad[]
  originalConfig: Config | null
  config: Config | null
  latestUploadTimestamp: number
  callouts: Upload[]
  deletedCount: number
  errorCode: number | null
  nextLink: string | null
  dashboardLink: string | null
  respondToResize: boolean
}

interface AppProps {
}

interface DeleteEvent {
  data: string
}

export default class App extends React.Component<AppProps, AppState> {
  eventSource?: EventSource

  static NUMBER_OF_EXAMPLE_IMAGES = 17

  constructor(props: AppProps) {
    super(props)

    this.state = {
      originalConfig: null,
      config: null,
      uploads: [],
      ads: [],
      callouts: [],
      latestUploadTimestamp: 0,
      deletedCount: 0,
      errorCode: null,
      nextLink: null,
      dashboardLink: null,
      respondToResize: true
    }

    this.onResize = this.onResize.bind(this)
  }

  extractGalleryCodeFromUrl() {
    const urlParams = new URLSearchParams(window.location.search)
    const codeFromQuery = urlParams.get("galleryCode")
    if (!codeFromQuery) {
      const pathComponents = window.location.pathname.split('/')
      return pathComponents[pathComponents.length - 1]
    }
    return codeFromQuery
  }

  listenForUploadDeletions() {
    this.eventSource = new EventSource(process.env.REACT_APP_SSE_URL as string)
    this.eventSource.addEventListener("upload.delete", (e: any) => this.handleUploadDeletionEvent(e))
  }

  handleUploadDeletionEvent(event: DeleteEvent) {
    let uploadCode = JSON.parse(event.data).code
    this.state.config && this.state.config.debug && console.log("SSE | Delete Upload: ", uploadCode)

    let uploads = [...this.state.uploads].filter(u => { return u.id !== uploadCode })
    if (uploads.length < this.state.uploads.length) {
      this.state.config && this.state.config.debug && console.log(`Removing ${this.state.uploads.length - uploads.length} upload`)
      if (uploads.length === 0) {
        uploads = Upload.getExamples(App.NUMBER_OF_EXAMPLE_IMAGES)
      }
      this.setState({
        uploads: uploads,
        deletedCount: this.state.deletedCount + 1
      })
    }
  }

  componentDidMount() {
    ReactGA.initialize((process.env.REACT_APP_GA_ID as string))
    ReactGA.pageview(window.location.pathname + window.location.search)

    this.listenForUploadDeletions()

    window.addEventListener('resize', this.onResize);

    let galleryCode = this.extractGalleryCodeFromUrl()
    if (galleryCode == null || galleryCode === "") {
      this.setState({ errorCode: 404 })
      return
    }

    Requests.getLiveFeed(galleryCode, (json: any) => {
      let originalConfig = new Config(json)

      //  Make a copy
      let overriddenConfig = JSON.parse(JSON.stringify(originalConfig))
      SearchParamUtil.overrideSettingsFromUrl(overriddenConfig)

      let ads = json.slideshow_ads

      let initialUploads = json.uploads
      if (initialUploads.length === 0) {
        initialUploads = Upload.getExamples(App.NUMBER_OF_EXAMPLE_IMAGES)
        overriddenConfig.debug && console.log(`Example uploads: ${initialUploads.length}`)
      }

      let merged = UploadUtil.mergeUploads(this.state.uploads, initialUploads, overriddenConfig.recentLimit)
      overriddenConfig.debug && console.log(`Initial uploads: ${merged.length}`)

      // Perform auto layout if applicable
      overriddenConfig = overriddenConfig.autoLayout ? this.applyAutoLayout(overriddenConfig) : overriddenConfig

      this.setState({
        originalConfig: originalConfig,
        config: overriddenConfig,
        ads: ads,
        uploads: merged,
        dashboardLink: json._links["dashboard:self"] ? json._links["dashboard:self"].href : null,
        latestUploadTimestamp: UploadUtil.getLatestTimestampFromUploads(merged),
        errorCode: overriddenConfig.enabled ? null : 401
      })
    }, (errorCode: number) => {
      this.setState({ errorCode: errorCode })
      console.error(errorCode)
      ReactGA.exception({ description: errorCode })
    })
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize, false)
  }

  onResize(_e: any) {
    if (!this.state.respondToResize
      || this.state.config === null
      || !this.state.config.autoLayout) {
      return
    }

    this.setState({
      respondToResize: false,
      config: this.applyAutoLayout({ ...this.state.config })
    })

    // Allow another resize event in 1/10 of a second
    setTimeout(() => {
      this.setState({ respondToResize: true })
    }, 100)
  }

  applyAutoLayout(config: Config) {
    let layout = UploadUtil.getAutolayout(this.state.uploads)
    config.rows = layout.rows
    config.columns = layout.columns
    return config
  }

  handleSettingsChange(newConfig: Config): void {
    newConfig = newConfig.autoLayout ? this.applyAutoLayout(newConfig) : newConfig

    this.setState({ config: newConfig })

    let pageTitle = `Live Feed | ${newConfig.galleryTitle}`

    let newPath = `/live-feed/${newConfig.galleryCode}`
    let paramsStr = SearchParamUtil.getUrlSearchParams(newConfig, this.state.originalConfig ?? newConfig).toString()
    if (paramsStr.length > 0) {
      newPath = `${newPath}?${paramsStr}`
    }

    window.history.replaceState(null, pageTitle, newPath)
    ReactGA.pageview(window.location.pathname + window.location.search)
  }

  fetchMoreUploads() {
    if (this.state.config === null) { return }
    let config = this.state.config

    // Fetch enough uploads to fill the screen and at least 25
    let fetchQuantity = Math.max(config.rows * config.columns, 25)

    // Start back at beginning to check for new uploads
    let link = this.state.nextLink
    if (!link || this.state.uploads.length >= this.state.config.recentLimit) {
      link = Requests.resolveInitialFetchLink(config.galleryCode, fetchQuantity)
    }

    config.debug && console.log(`Fetching ${fetchQuantity} uploads from ${link}`)

    Requests.fetchNextUploads(link, (json: any) => {
      let currentUploads = [...this.state.uploads]

      if (JSON.stringify(this.state.ads) !== JSON.stringify(json.slideshow_ads)) {
        config.debug && console.log("Ads changed, updating")
        this.setState({ ads: json.slideshow_ads })
      }

      // Remove example images if we get at least one real upload
      if (json.uploads.length > 0) {
        config.debug && console.log(`Removing example images`)
        currentUploads = currentUploads.filter(upload => { return !upload.id.includes(Upload.EXAMPLE_ID_PREFIX) })
      }
      config.debug && console.log(`Received ${json.uploads.length} uploads to merge into current set of ${currentUploads.length}`)

      let merged = UploadUtil.mergeUploads(currentUploads, json.uploads, config.recentLimit)

      let callouts: Upload[] = []
      if (config.featureNewImages) {
        let recentlyAdded = merged.filter(upload => { return upload.timestamp > this.state.latestUploadTimestamp })
        config.debug && console.log(`Received ${recentlyAdded.length} callouts to display`)
        callouts = UploadUtil.mergeUploads(this.state.callouts, recentlyAdded, config.recentLimit)
      }

      this.setState({
        uploads: merged,
        callouts: callouts,
        latestUploadTimestamp: UploadUtil.getLatestTimestampFromUploads(merged),
        nextLink: json._links.next ? json._links.next.href : null
      })
    }, (errorCode: number) => {
      // Gracefully keep displaying current photos
      config.debug && console.error(errorCode)
      ReactGA.exception({ description: errorCode })
    })
  }

  render() {
    if (this.state.errorCode != null) {
      return <ErrorView code={this.state.errorCode} />
    }

    let config = this.state.config
    if (config === null) {
      return <Spinner color="#30AADE" centerVertical />
    }

    return (
      <div className="container" style={{ backgroundColor: config.backgroundColor }}>
        <DisplayController
          config={config}
          ads={this.state.ads}
          availableUploads={this.state.uploads}
          didShowNewGroup={() => this.fetchMoreUploads()}
          deletedCount={this.state.deletedCount}
          callouts={this.state.callouts}
          didDisplayCallouts={() => this.setState({ callouts: [] })}
        />
        {!config.hideControls &&
          <Controls
            config={config}
            numberOfAds={this.state.ads.length}
            dashboardLink={this.state.dashboardLink}
            didChangeConfig={(newConfig: Config) => this.handleSettingsChange(newConfig)}
          />
        }
      </div>
    )
  }
}
